/**
 * The ONLY way to build internal URLs in this app.
 *
 * Pages must never construct a URL by string-concatenation like
 * `'/' + tenantSlug + '/chat'` — it bypasses the vanity-domain abstraction
 * in `useActiveTenant` and risks the same class of bugs the path-based
 * refactor was meant to kill.
 *
 * NOTE: vanity domains are LIVE in production on jewishpro.ai. The
 * implementation here was forward-ported from `legacy` commit f4ec4bd4
 * (the source of the running prod build) after the legacy -> main
 * migration dropped it. See docs/ENVIRONMENTS.md -> "Vanity domains".
 *
 * Two flavors:
 *   - `buildPath(slug, '/chat')` — when you already know the slug
 *     (e.g. post-login navigation that uses `user.company_tenant_code`)
 *   - `useTenantPath()` — inside a tenant route, returns a builder
 *     bound to the current `useActiveTenant().slug`
 */

import { useCallback } from 'react';
import { useActiveTenant } from './useActiveTenant';
import { useAuth } from '@/hooks/useAuth';
import { getCachedHostSlug } from './vanityHost';

/**
 * The platform-owner tenant slug. The bare URL (`/`) and any
 * anonymous request that lacks a tenant context resolves to this
 * tenant's branded login (`/<DEFAULT_TENANT_SLUG>/login`). There is
 * NO generic, tenant-less /login page — every login renders the
 * brand of the tenant in its URL.
 *
 * The platform owner (CorpBrain) is itself a tenant; its branded
 * login serves as the catch-all for visitors who arrive without a
 * tenant link.
 */
export const DEFAULT_TENANT_SLUG = 'corpbrain-admin';

/**
 * The default branded login path. Use this for: bare-URL anonymous
 * landing, post-logout fallback, 401 redirect when no tenant context
 * exists in the URL.
 */
export const DEFAULT_LOGIN_PATH = '/' + DEFAULT_TENANT_SLUG + '/login';

/**
 * The login path to use when there's NO tenant context in the URL —
 * the bare-`/` root redirect and the catch-all both funnel here.
 *
 * On a vanity host the platform default (`/corpbrain-admin/login`)
 * would be WRONG twice over: it names the wrong tenant AND, because the
 * slug-hiding history only strips the host tenant's own prefix, that
 * foreign slug would leak into the visible URL. So when the current
 * host resolves (synchronously, from cache) to a tenant, return THAT
 * tenant's login instead. Falls back to the platform default on normal
 * hosts. Keep this a plain function (not a hook) so non-React redirect
 * targets can call it.
 */
export function loginPathForHost(): string {
  try {
    const slug = getCachedHostSlug(window.location.host);
    if (slug) return '/' + slug + '/login';
  } catch {
    /* ignore — fall back to the platform default */
  }
  return DEFAULT_LOGIN_PATH;
}

/**
 * Build a tenant-scoped path from a leading-slash route fragment.
 * `slug === ''` falls back to root-level (used by reserved-route
 * pages — they shouldn't be calling this, but the fallback is safe).
 */
export function buildPath(slug: string, suffix: string): string {
  const cleanSuffix = suffix.startsWith('/') ? suffix : '/' + suffix;
  if (!slug) return cleanSuffix;
  return '/' + slug.toLowerCase() + cleanSuffix;
}

/**
 * Hook returning a builder for the current tenant. Use this in pages
 * nested under `/:tenantSlug/*` — `useTenantPath()('/chat')` yields
 * e.g. `/team-alpha/chat`.
 *
 * Fallback: if no tenant slug is in the URL (e.g. a brief moment
 * during an inter-route redirect), the builder uses the SIGNED-IN
 * user's `company_tenant_code` so nav links still point somewhere
 * sensible.
 */
export function useTenantPath(): (suffix: string) => string {
  const { slug } = useActiveTenant();
  const { user } = useAuth();
  const fallbackSlug = (user?.company_tenant_code || user?.tenant_code || '').toLowerCase();
  const effectiveSlug = slug || fallbackSlug;
  return useCallback(
    (suffix: string) => buildPath(effectiveSlug, suffix),
    [effectiveSlug],
  );
}

/**
 * Branded login URL for a given tenant. Falls back to the platform
 * default tenant's login when no slug is provided — there is no
 * tenant-less /login route.
 */
export function buildTenantLoginPath(slug: string): string {
  if (!slug) return DEFAULT_LOGIN_PATH;
  return buildPath(slug, '/login');
}

/**
 * The home URL for a user — uses their `company_tenant_code` (or own
 * `tenant_code` for standalone tenants). For super_admin we ALSO
 * return their tenant home (corpbrain-admin in the default seed):
 * super_admin's "personal" home is the agents/chat surface with the
 * "Shalom" greeting, same as every other user — they get to the
 * super-admin tools via the sidebar's explicit "Super Admin" nav
 * item, which routes to /<their-tenant>/super-admin.
 *
 * Every authenticated path MUST contain a tenant slug. A user with
 * no tenant context at all is treated as broken auth and sent back
 * to /login to re-establish.
 */
export function buildUserHomePath(user: {
  role?: string;
  user_role?: string;
  company_tenant_code?: string | null;
  tenant_code?: string;
} | null | undefined): string {
  if (!user) return DEFAULT_LOGIN_PATH;
  const slug = (user.company_tenant_code || user.tenant_code || '').toLowerCase();
  if (slug) return '/' + slug + '/';
  return DEFAULT_LOGIN_PATH;
}
